/**
 * What the page URL asks for. Every switch of the kiosk is here, with its default:
 *
 *   ?session=<id>          the session id (default: a new random one)
 *   ?operator=1            join that session as a remote operator (needs ?session=)
 *   ?replay=<id>           play back a recorded session (a trajectory id or a session id)
 *   ?review=<run>          review a gym run (&by=human:<name>, &strategy=blind|review|…, &item=<id>); the gym serves
 *                          it on /gym (python -m eval.gym serve), each item replayed as its episode was played
 *   ?theme=<theme>         start with this theme (classico, essenziale)
 *   ?ui=<preset>           start with this preset (kiosk, text, main-bar-top, content-first, essenziale, …)
 *   ?mode=<mode>           force an accessibility mode (none, visual, hearing, motor, cognitive)
 *   ?dev=1                 open the dev panel (also Ctrl+D)
 *   ?avatar=0              no MuseTalk avatar
 *   ?avatar_plate=<id>     MuseTalk "solo bocca" with this plate
 *   ?bg3d=1                the low-poly three.js avatar as background (avatar-lowpoly on :5666)
 *   ?aec=os|browser|off    who cancels the echo (on the totem: os, the eMeet speakerphone); without it, the
 *                          backend's declared canceller decides (the `audio_chain` voice frame)
 *   ?speak=1               keep the voice channel open with the microphone off: typed turns are spoken
 *   ?zoom=0|1              pinch-zoom off or on; default: off on the totem (?aec=os says it is the totem), on
 *                          everywhere else (on a phone it is an accessibility feature)
 */
import { accessibilityModes, type AccessibilityMode } from './wire';

export type AecMode = 'os' | 'browser' | 'off';

export interface ReviewFlags {
  run: string;
  by: string;
  strategy: string;
  item: string | null;
}

export interface Flags {
  sessionId: string | null;
  role: 'kiosk' | 'operator' | 'replay' | 'review';
  replayId: string | null;
  review: ReviewFlags | null;
  theme: string | null;
  preset: string | null;
  mode: AccessibilityMode | null;
  dev: boolean;
  avatar: boolean;
  avatarPlate: string | null;
  lowpoly: boolean;
  aec: AecMode;
  /** The URL said `?aec=` (or `?osaec=1`): it wins over the backend's declared canceller. */
  aecFromUrl: boolean;
  speak: boolean;
  zoom: boolean;
}

const SAFE_ID = /^[A-Za-z0-9_.:-]+$/;
const SAFE_ITEM = /^[A-Za-z0-9_.:|-]+$/; // a review item is "<episode>|<target>"

/** A replay or a review: the screen plays a recording and sends nothing. */
export function playsRecording(role: Flags['role']): boolean {
  return role === 'replay' || role === 'review';
}

export function readFlags(search: string, defaults: { aec?: AecMode } = {}): Flags {
  const q = new URLSearchParams(search);
  const id = (name: string) => {
    const v = q.get(name);
    return v && SAFE_ID.test(v) ? v : null;
  };
  const replayId = id('replay');
  const reviewRun = id('review');
  const item = q.get('item');
  const operator = q.get('operator') === '1';
  const mode = q.get('mode');
  const aec = q.get('aec');
  return {
    sessionId: id('session'),
    role: reviewRun ? 'review' : replayId ? 'replay' : operator ? 'operator' : 'kiosk',
    replayId,
    review: reviewRun
      ? { run: reviewRun, by: id('by') ?? 'human:anonymous', strategy: id('strategy') ?? 'blind', item: item && SAFE_ITEM.test(item) ? item : null }
      : null,
    theme: id('theme'),
    preset: id('ui'),
    mode: (accessibilityModes as readonly string[]).includes(mode ?? '') ? (mode as AccessibilityMode) : null,
    dev: q.get('dev') === '1',
    avatar: q.get('avatar') !== '0',
    avatarPlate: id('avatar_plate'),
    lowpoly: q.get('bg3d') === '1',
    aec: aec === 'os' || aec === 'browser' || aec === 'off' ? aec : q.get('osaec') === '1' ? 'os' : defaults.aec ?? 'browser',
    aecFromUrl: aec === 'os' || aec === 'browser' || aec === 'off' || q.get('osaec') === '1',
    speak: q.get('speak') === '1',
    zoom: q.get('zoom') === '1' || (q.get('zoom') !== '0' && !(aec === 'os' || q.get('osaec') === '1')),
  };
}

export function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** The link an operator opens to join this session. */
export function operatorLink(origin: string, sessionId: string): string {
  return `${origin}/?operator=1&session=${encodeURIComponent(sessionId)}`;
}
